import { logout } from 'actions/auth'
import Bill from 'components/Icons/Bill'
import IntegrationsIcon from 'components/Icons/Integrations'
import LocationMarker from 'components/Icons/LocationMarker'
import Lock from 'components/Icons/Lock'
import Logout from 'components/Icons/Logout'
import ThanxLogo from 'components/Icons/ThanxLogo'
import UserSupport from 'components/Icons/UserSupport'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import useIsAdmin from 'hooks/useIsAdmin'
import useIsThanxAdmin from 'hooks/useIsThanxAdmin'
import { useRbacNavDisabledWithReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { Fields as Merchant } from 'models/Merchant'
import React from 'react'
import { isNonProductionEnvironment } from 'utilities/urlUtils'
import Divider from '../Bar/Divider'
import NavLink from '../Bar/NavLink'
import LiveUpdatesDevToolsToggle from './LiveUpdatesDevToolsToggle'
import MerchantSelector from './MerchantSelector'

const t = buildTranslate('nav.profile_menu')

type Props = {
  merchant: Merchant
  isLeftNav?: boolean
  onClick?: () => void
}

const Body = (props: Props) => {
  const { isLeftNav = false, merchant, onClick = () => null } = props

  const isThanxAdmin = useIsThanxAdmin()
  const merchantUsers = useRbacNavDisabledWithReason('MerchantUsers')
  const locations = useRbacNavDisabledWithReason('Locations')
  const userSupport = useRbacNavDisabledWithReason('UserSupport')
  const billing = useRbacNavDisabledWithReason('Billing')
  const rbacEnabled = useFlag('role-based-access-control', false)
  const tooltipsEnabled = useFlag('rbac-permission-tooltips', false)
  const operationsCenterEnabled = useFlag('operations-center', false)
  // Integrations has no dedicated backend permission resource — it's admin-only
  // by design. useIsAdmin is the canonical "is current user an admin" check
  // (legacy is_admin OR RBAC preset Admin role) so the nav and ProtectedRoute
  // share the same source of truth.
  const integrationsDisabled = !useIsAdmin()
  const integrationsReason =
    integrationsDisabled && rbacEnabled && tooltipsEnabled
      ? t('integrations_admin_only')
      : undefined
  const dispatch = useDispatch()
  const onLogout = () => {
    onClick()
    dispatch(logout())
  }

  return (
    <>
      {isThanxAdmin && (
        <NavLink
          onClick={onClick}
          isLeftNav={isLeftNav}
          text={t('thanx_admin')}
          Icon={ThanxLogo}
          colorName="spearmint10"
          link="/choose"
        />
      )}
      {isNonProductionEnvironment() && (
        <LiveUpdatesDevToolsToggle isLeftNav={isLeftNav} />
      )}
      {!operationsCenterEnabled && (
        <NavLink
          onClick={onClick}
          isLeftNav={isLeftNav}
          text={t('locations')}
          Icon={LocationMarker}
          link="/locations"
          disabled={locations.disabled}
          disabledReason={locations.disabledReason}
          tooltipPlacement="left"
        />
      )}
      <NavLink
        onClick={onClick}
        isLeftNav={isLeftNav}
        text={t('integrations')}
        Icon={IntegrationsIcon}
        link="/integrations"
        disabled={integrationsDisabled}
        disabledReason={integrationsReason}
        tooltipPlacement="left"
      />

      {!operationsCenterEnabled && (
        <NavLink
          onClick={onClick}
          isLeftNav={isLeftNav}
          text={t('user_support')}
          Icon={UserSupport}
          link="/user_support"
          disabled={userSupport.disabled}
          disabledReason={userSupport.disabledReason}
          tooltipPlacement="left"
        />
      )}
      <NavLink
        onClick={onClick}
        isLeftNav={isLeftNav}
        text={t('billing')}
        Icon={Bill}
        link="/billing"
        disabled={billing.disabled}
        disabledReason={billing.disabledReason}
        tooltipPlacement="left"
      />
      <NavLink
        onClick={onClick}
        isLeftNav={isLeftNav}
        text={t('access')}
        Icon={Lock}
        link="/merchant_users"
        disabled={merchantUsers.disabled}
        disabledReason={merchantUsers.disabledReason}
        tooltipPlacement="left"
      />
      {!isThanxAdmin && (
        <MerchantSelector
          currentMerchantId={merchant.id}
          onClick={() => {
            // Empty onClick required for MenuItem logic
          }}
        />
      )}
      <Divider />
      <NavLink
        onClick={onLogout}
        isLeftNav={isLeftNav}
        text={t('log_out')}
        Icon={Logout}
      />
    </>
  )
}

export default Body
